import { useInputControl, type FieldMetadata } from '@conform-to/react';

import { Label, type LabelProps } from '~/components/ui/label.tsx';
import { cn } from '~/styles/extended-theme.ts';

import { Checkbox, type CheckboxProps } from '../ui/checkbox.tsx';

import { FieldErrors } from './form-errors.tsx';

export interface CheckboxFieldProps {
  className?: string;
  field: FieldMetadata<boolean>;
  label?: string;
  labelProps?: Omit<LabelProps, 'htmlFor'>;
  checkboxProps?: Omit<CheckboxProps, 'name'>;
  disabled?: boolean;
}

export function CheckboxField({
  field,
  label,
  labelProps,
  checkboxProps,
  className,
}: CheckboxFieldProps) {
  const checkedValue = field.value ?? 'on';
  // To emulate native events that Conform listen to:
  // See https://conform.guide/integrations
  const control = useInputControl(field);

  const { className: labelClassName, ...restLabelProps } = labelProps ?? {};

  return (
    <div className={className}>
      {/* see https://github.com/radix-ui/primitives/issues/2402 */}
      <div className="flex gap-2">
        <Checkbox
          {...checkboxProps}
          id={field.id}
          aria-invalid={field.errorId ? true : undefined}
          aria-describedby={field.errorId}
          checked={control.value === checkedValue}
          onCheckedChange={(state) => {
            control.change(state.valueOf() ? checkedValue : '');
            checkboxProps?.onCheckedChange?.(state);
          }}
          onFocus={(event) => {
            control.focus();
            checkboxProps?.onFocus?.(event);
          }}
          onBlur={(event) => {
            control.blur();
            checkboxProps?.onBlur?.(event);
          }}
          type="button"
        />
        <Label className={cn('font-bold', labelClassName)} htmlFor={field.id} {...restLabelProps}>
          {label}
        </Label>
      </div>
      <div className="pt-1">
        {field.errorId ? <FieldErrors id={field.errorId} errors={field.errors} /> : null}
      </div>
    </div>
  );
}
